import { supabase } from '../config/supabase';
import { TABLES } from '../data/tables';
import { BusinessListingForm, BusinessListingResult } from '../data/types/application';
import { Business, ServiceLocation } from '../data/types/domain';

const removeIds = <T extends object>(obj: T): Partial<T> => {
  const newObj = { ...obj };
  delete (newObj as any).id;
  delete (newObj as any).created_at;
  delete (newObj as any).updated_at;
  delete (newObj as any).business_id;
  return newObj;
};

export const businessService = {

  async getBusinessListings(): Promise<BusinessListingForm[]> {
    const { data, error } = await supabase
      .from(TABLES.BUSINESS)
      .select('*');

    if (error) throw error;
    return data || [];
  },

  async getBusinessListingsByUserId(userId: string): Promise<BusinessListingResult> {
    const { data, error } = await supabase
      .from(TABLES.BUSINESS)
      .select('*')
      .eq('user_owner', userId)
      .single();

    if (error) throw error;
    const businessListing = await this.getBusinessById(data.id);
    return businessListing;
  },

  // TODO: Refactor this to getBusinessById
  async getBusinessByBusinessId(businessId: number): Promise<Business> {
    const { data, error } = await supabase
      .from(TABLES.BUSINESS)
      .select('*')
      .eq('id', businessId)
      .single();

    if (error) throw error;
    return data;
  },

  async getBusinessById(id: number): Promise<BusinessListingResult> {
    const { data, error } = await supabase
      .from(TABLES.BUSINESS)
      .select(`
        *,
        listing_policy:${TABLES.LISTING_POLICY}(*),
        shipping_policy:${TABLES.SHIPPING_POLICY}(*),
        service_location:${TABLES.SERVICE_LOCATION}(*)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;

    return {
      business: {
        id: data.id,
        name: data.name,
        description: data.description,
        contact: data.contact,
        email: data.email,
        address: data.address,
        postcode: data.postcode,
        owner: data.owner,
        user_owner: data.user_owner,
        logo: data.logo,
        website: data.website,
        facebook: data.facebook,
        instagram: data.instagram,
        twitter: data.twitter,
        youtube: data.youtube,
        google_rating: data.google_rating,
        google_reviews_count: data.google_reviews_count,
        google_reviews_url: data.google_reviews_url,
        trustpilot_rating: data.trustpilot_rating,
        trustpilot_reviews_count: data.trustpilot_reviews_count,
        trustpilot_reviews_url: data.trustpilot_reviews_url,
        created_at: data.created_at,
        updated_at: data.updated_at,
      },
      listingPolicy: data.listing_policy[0],
      shippingPolicies: data.shipping_policy || [],
      serviceLocations: data.service_location || [],
    };
  },

  async createBusiness(businessListing: BusinessListingForm): Promise<Business> {
    const { business, listingPolicy, shippingPolicies, serviceLocations } = businessListing;

    // First create the business
    const { data: businessData, error: businessError } = await supabase
      .from(TABLES.BUSINESS)
      .insert([{
        ...business,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }])
      .select()
      .single();

    if (businessError) throw businessError;
    return businessData;
  },

  async updateBusiness(id: number, business: Partial<Business>): Promise<Business> {

    // Update business
    const { data: updatedBusiness, error: businessError } = await supabase
      .from(TABLES.BUSINESS)
      .update(removeIds(business))
      .eq('id', id)
      .select()
      .single();

    if (businessError) throw businessError;

    return updatedBusiness;
  },

  async deleteBusiness(id: number): Promise<void> {
    const { error } = await supabase
      .from(TABLES.BUSINESS)
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async getServiceLocations(businessId: number): Promise<ServiceLocation[]> {
    const { data, error } = await supabase
      .from(TABLES.SERVICE_LOCATION)
      .select(`
        *,
        served_countries:${TABLES.COUNTRY}(country_id),
        served_cities:${TABLES.CITY}(city_id)
      `)
      .eq('business_id', businessId);

    if (error) throw error;
    return data || [];
  },
}; 